import React, { createContext, useContext, useReducer, useEffect } from 'react';
import storage from '../utils/storage';
import { spacedRepetitionSystem } from '../services/spacedRepetitionService';
import { firestoreSyncService } from '../services/firestoreSyncService';
import { getFirestore, doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { initializeApp, getApp } from 'firebase/app';
import { firebaseConfig } from '../../firebase.config';

// Initialize Firebase if not already initialized
let app;
try {
  app = getApp();
} catch (error) {
  app = initializeApp(firebaseConfig);
}
const db = getFirestore(app);
const auth = getAuth(app);

// Action Types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_USER: 'SET_USER',
  SET_ONBOARDED: 'SET_ONBOARDED',
  ADD_CLASS: 'ADD_CLASS',
  UPDATE_CLASS: 'UPDATE_CLASS',
  DELETE_CLASS: 'DELETE_CLASS',
  SET_CURRENT_CLASS: 'SET_CURRENT_CLASS',
  SET_CURRENT_SET: 'SET_CURRENT_SET',
  ADD_FLASHCARD_SET: 'ADD_FLASHCARD_SET',
  UPDATE_FLASHCARD_SET: 'UPDATE_FLASHCARD_SET',
  DELETE_FLASHCARD_SET: 'DELETE_FLASHCARD_SET',
  ADD_FLASHCARD: 'ADD_FLASHCARD',
  UPDATE_FLASHCARD: 'UPDATE_FLASHCARD',
  DELETE_FLASHCARD: 'DELETE_FLASHCARD',
  START_STUDY_SESSION: 'START_STUDY_SESSION',
  END_STUDY_SESSION: 'END_STUDY_SESSION',
  UPDATE_CARD_PROGRESS: 'UPDATE_CARD_PROGRESS',
  UPDATE_STREAK: 'UPDATE_STREAK',
  UPDATE_STUDY_TIME: 'UPDATE_STUDY_TIME',
  UPDATE_SET_STATS: 'UPDATE_SET_STATS',
  MARK_CLASS_FINISHED: 'MARK_CLASS_FINISHED',
  INCREMENT_LIFELONG_CARDS: 'INCREMENT_LIFELONG_CARDS',
  INCREMENT_LIFELONG_SETS: 'INCREMENT_LIFELONG_SETS',
  INCREMENT_LIFELONG_CLASSES: 'INCREMENT_LIFELONG_CLASSES',
  MOVE_SET_TO_STUDIED: 'MOVE_SET_TO_STUDIED',
  MOVE_SET_TO_ACTIVE: 'MOVE_SET_TO_ACTIVE',
  ADD_MULTIPLE_FLASHCARDS: 'ADD_MULTIPLE_FLASHCARDS',
  LOAD_DATA: 'LOAD_DATA',
  USE_AI_FLASHCARDS: 'USE_AI_FLASHCARDS',
  UPDATE_AI_FLASHCARD_COUNTS: 'UPDATE_AI_FLASHCARD_COUNTS',
  CLEAR_ALL_DATA: 'CLEAR_ALL_DATA',
  UPDATE_SUBSCRIPTION_STATUS: 'UPDATE_SUBSCRIPTION_STATUS',
};

// Initial State with Neuroscience-Based Structure
const initialState = {
  // User & Onboarding
  user: {
    name: '',
    email: '',
    isOnboarded: false,
    streak: 0,
    totalStudyTime: 0, // in minutes
    lastStudyDate: null,
    // Lifelong stats (only increase, never decrease)
    lifelongCardsCreated: 0,
    lifelongSetsCreated: 0,
    lifelongClassesCreated: 0,
    // AI flashcard tracking - defaults to 0 in local state
    // The 150 free AI flashcards are granted ONLY during signup in AuthContext
    // This default is just for initial state - actual value comes from Firestore or signup
    ai_flashcards_earned: 0,
    ai_flashcards_used: 0,
    // Subscription status
    isPro: false,
    subscriptionType: null,
    subscriptionPlatform: null,
    subscriptionExpirationDate: null,
    preferences: {
      sessionLength: 20, // minutes
      difficulty: 'medium',
      notifications: true,
    },
  },
  
  // Classes (Subjects)
  classes: [],
  currentClass: null,
  
  // Flashcard Sets
  flashcardSets: [],
  studiedSets: [], // Sets that have been studied and are scheduled for future review
  currentSet: null,
  
  // Individual Flashcards
  flashcards: [],
  
  // Study Session (Neuroscience-Based)
  studySession: {
    isActive: false,
    currentCard: null,
    cardsToReview: [],
    sessionStartTime: null,
    cardsStudied: 0,
    correctAnswers: 0,
    sessionProgress: 0,
  },
  
  // Spaced Repetition Data
  cardProgress: {}, // cardId -> { ease, interval, repetitions, nextReview }
  
  // App State
  loading: false,
  error: null,
};

// Reducer with Neuroscience-Based Logic
const flashcardReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case ActionTypes.SET_USER:
      return { ...state, user: { ...state.user, ...action.payload } };
    
    case ActionTypes.SET_ONBOARDED:
      return { 
        ...state, 
        user: { ...state.user, isOnboarded: action.payload } 
      };
    
    case ActionTypes.ADD_CLASS:
      const newClass = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: action.payload.name,
        color: action.payload.color,
        createdAt: new Date().toISOString(),
        totalSets: 0,
        totalCards: 0,
        lastStudied: null,
      };
      return { 
        ...state, 
        classes: [...state.classes, newClass],
        user: { ...state.user, lifelongClassesCreated: (state.user.lifelongClassesCreated || 0) + 1 }
      };
    
    case ActionTypes.UPDATE_CLASS:
      return {
        ...state,
        classes: state.classes.map(cls => 
          cls.id === action.payload.id ? { ...cls, ...action.payload } : cls
        ),
      };
    
    case ActionTypes.DELETE_CLASS:
      return {
        ...state,
        classes: state.classes.filter(cls => cls.id !== action.payload),
        currentClass: state.currentClass?.id === action.payload ? null : state.currentClass,
      };
    
    case ActionTypes.SET_CURRENT_CLASS:
      return { ...state, currentClass: action.payload };
    
    case ActionTypes.SET_CURRENT_SET:
      return { ...state, currentSet: action.payload };
    
    case ActionTypes.ADD_FLASHCARD_SET:
      const newSet = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        classId: action.payload.classId,
        name: action.payload.name,
        description: action.payload.description || '',
        color: action.payload.color || '#4A90E2',
        createdAt: new Date().toISOString(),
        totalCards: 0,
        timesPlayed: 0,
        successRate: 0,
        lastStudied: null,
        isAIGenerated: action.payload.isAIGenerated || false,
      };
      
      // Update class statistics after adding new set - count both active and studied sets
      const updatedClassesWithNewSet = state.classes.map(cls => {
        const classActiveSets = [...state.flashcardSets, newSet].filter(set => set.classId === cls.id);
        const classStudiedSets = (state.studiedSets || []).filter(set => set.classId === cls.id);
        const allClassSets = [...classActiveSets, ...classStudiedSets];
        const totalCards = allClassSets.reduce((sum, set) => sum + (set.totalCards || 0), 0);
        const totalSets = allClassSets.length;
        const lastStudied = allClassSets
          .filter(set => set.lastStudied)
          .sort((a, b) => new Date(b.lastStudied) - new Date(a.lastStudied))[0]?.lastStudied || null;
        
        return {
          ...cls,
          totalCards,
          totalSets,
          lastStudied,
        };
      });
      
      return { 
        ...state, 
        flashcardSets: [...state.flashcardSets, newSet],
        classes: updatedClassesWithNewSet,
        user: { ...state.user, lifelongSetsCreated: (state.user.lifelongSetsCreated || 0) + 1 }
      };
    
    case ActionTypes.UPDATE_FLASHCARD_SET:
      return {
        ...state,
        flashcardSets: state.flashcardSets.map(set => 
          set.id === action.payload.id ? { ...set, ...action.payload } : set
        ),
      };
    
    case ActionTypes.DELETE_FLASHCARD_SET:
      const remainingSets = state.flashcardSets.filter(set => set.id !== action.payload);
      const remainingStudiedSets = (state.studiedSets || []).filter(set => set.id !== action.payload);
      
      // Update class statistics after deleting set - count both active and studied sets
      const updatedClassesAfterDelete = state.classes.map(cls => {
        const classActiveSets = remainingSets.filter(set => set.classId === cls.id);
        const classStudiedSets = remainingStudiedSets.filter(set => set.classId === cls.id);
        const allClassSets = [...classActiveSets, ...classStudiedSets];
        const totalCards = allClassSets.reduce((sum, set) => sum + (set.totalCards || 0), 0);
        const totalSets = allClassSets.length;
        const lastStudied = allClassSets
          .filter(set => set.lastStudied)
          .sort((a, b) => new Date(b.lastStudied) - new Date(a.lastStudied))[0]?.lastStudied || null;
        
        return {
          ...cls,
          totalCards,
          totalSets,
          lastStudied,
        };
      });
      
      return {
        ...state,
        flashcardSets: remainingSets,
        studiedSets: remainingStudiedSets,
        currentSet: state.currentSet?.id === action.payload ? null : state.currentSet,
        classes: updatedClassesAfterDelete,
      };
    
    case ActionTypes.ADD_FLASHCARD:
      const newCard = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        setId: action.payload.setId,
        front: action.payload.front,
        back: action.payload.back,
        hint: action.payload.hint || '',
        difficulty: action.payload.difficulty || 'medium',
        createdAt: new Date().toISOString(),
        // Spaced Repetition Fields
        ease: 2.5, // SM-2 algorithm starting ease
        interval: 1, // days
        repetitions: 0,
        nextReview: new Date().toISOString(),
        totalReviews: 0,
        correctReviews: 0,
      };
      
      // Update set's totalCards count
      const updatedSets = state.flashcardSets.map(set => 
        set.id === action.payload.setId 
          ? { ...set, totalCards: (set.totalCards || 0) + 1 }
          : set
      );
      
      // Update class statistics - count both active and studied sets
      const updatedClasses = state.classes.map(cls => {
        const classActiveSets = updatedSets.filter(set => set.classId === cls.id);
        const classStudiedSets = (state.studiedSets || []).filter(set => set.classId === cls.id);
        const allClassSets = [...classActiveSets, ...classStudiedSets];
        const totalCards = allClassSets.reduce((sum, set) => sum + (set.totalCards || 0), 0);
        const totalSets = allClassSets.length;
        
        return {
          ...cls,
          totalCards,
          totalSets,
        };
      });
      
      return { 
        ...state, 
        flashcards: [...state.flashcards, newCard],
        flashcardSets: updatedSets,
        classes: updatedClasses,
        user: { ...state.user, lifelongCardsCreated: (state.user.lifelongCardsCreated || 0) + 1 }
      };
    
    case ActionTypes.UPDATE_FLASHCARD:
      return {
        ...state,
        flashcards: state.flashcards.map(card => 
          card.id === action.payload.id ? { ...card, ...action.payload } : card
        ),
      };
    
    case ActionTypes.DELETE_FLASHCARD:
      const cardToDelete = state.flashcards.find(card => card.id === action.payload);
      const updatedSetsAfterDelete = cardToDelete 
        ? state.flashcardSets.map(set => 
            set.id === cardToDelete.setId 
              ? { ...set, totalCards: Math.max(0, (set.totalCards || 0) - 1) }
              : set
          )
        : state.flashcardSets;
      
      // Update class statistics - count both active and studied sets
      const updatedClassesAfterCardDelete = state.classes.map(cls => {
        const classActiveSets = updatedSetsAfterDelete.filter(set => set.classId === cls.id);
        const classStudiedSets = (state.studiedSets || []).filter(set => set.classId === cls.id);
        const allClassSets = [...classActiveSets, ...classStudiedSets];
        const totalCards = allClassSets.reduce((sum, set) => sum + (set.totalCards || 0), 0);
        const totalSets = allClassSets.length;
        
        return {
          ...cls,
          totalCards,
          totalSets,
        };
      });
      
      return {
        ...state,
        flashcards: state.flashcards.filter(card => card.id !== action.payload),
        flashcardSets: updatedSetsAfterDelete,
        classes: updatedClassesAfterCardDelete,
      };
    
    case ActionTypes.ADD_MULTIPLE_FLASHCARDS:
      const { setId: bulkSetId, flashcards: flashcardsToAdd } = action.payload;
      const newCards = flashcardsToAdd.map((card, index) => ({
        id: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
        setId: bulkSetId,
        front: card.front,
        back: card.back,
        hint: card.hint || '',
        difficulty: card.difficulty || 'medium',
        createdAt: new Date().toISOString(),
        // Spaced Repetition Fields
        ease: 2.5,
        interval: 1,
        repetitions: 0,
        nextReview: new Date().toISOString(),
        totalReviews: 0,
        correctReviews: 0,
      }));
      
      // Update set's totalCards count
      const updatedSetsBulk = state.flashcardSets.map(set => 
        set.id === bulkSetId 
          ? { ...set, totalCards: (set.totalCards || 0) + flashcardsToAdd.length }
          : set
      );
      
      // Update class statistics - count both active and studied sets
      const updatedClassesBulk = state.classes.map(cls => {
        const classActiveSets = updatedSetsBulk.filter(set => set.classId === cls.id);
        const classStudiedSets = (state.studiedSets || []).filter(set => set.classId === cls.id);
        const allClassSets = [...classActiveSets, ...classStudiedSets];
        const totalCards = allClassSets.reduce((sum, set) => sum + (set.totalCards || 0), 0);
        const totalSets = allClassSets.length;
        
        return {
          ...cls,
          totalCards,
          totalSets,
        };
      });
      
      return { 
        ...state, 
        flashcards: [...state.flashcards, ...newCards],
        flashcardSets: updatedSetsBulk,
        classes: updatedClassesBulk,
        user: { ...state.user, lifelongCardsCreated: (state.user.lifelongCardsCreated || 0) + flashcardsToAdd.length }
      };
    
    case ActionTypes.START_STUDY_SESSION:
      const cardsToReview = getCardsForReview(state.flashcards, state.cardProgress);
      return {
        ...state,
        studySession: {
          isActive: true,
          currentCard: cardsToReview[0] || null,
          cardsToReview,
          sessionStartTime: new Date().toISOString(),
          cardsStudied: 0,
          correctAnswers: 0,
          sessionProgress: 0,
        },
      };
    
    case ActionTypes.END_STUDY_SESSION:
      // Calculate session duration safely
      let sessionDurationMinutes = 0;
      if (state.studySession.sessionStartTime) {
        try {
          const sessionStart = new Date(state.studySession.sessionStartTime);
          const sessionEnd = new Date();
          const sessionDuration = sessionEnd - sessionStart;
          
          // Only add time if duration is valid and reasonable (not negative, not more than 24 hours)
          if (sessionDuration > 0 && sessionDuration < 86400000) { // 24 hours in milliseconds
            sessionDurationMinutes = Math.round(sessionDuration / 60000);
          }
        } catch (error) {
          console.error('Error calculating session duration:', error);
        }
      }
      
      // Ensure totalStudyTime is a valid number and in minutes
      const currentTotalTime = Number(state.user.totalStudyTime) || 0;
      // If currentTotalTime is suspiciously large (likely in milliseconds), convert it
      const normalizedTotalTime = currentTotalTime > 1000000 
        ? Math.round(currentTotalTime / 60000) 
        : currentTotalTime;
      
      const newTotalTime = normalizedTotalTime + sessionDurationMinutes;
      
      return {
        ...state,
        studySession: {
          isActive: false,
          currentCard: null,
          cardsToReview: [],
          sessionStartTime: null,
          cardsStudied: 0,
          correctAnswers: 0,
          sessionProgress: 0,
        },
        user: {
          ...state.user,
          totalStudyTime: newTotalTime,
          lastStudyDate: new Date().toISOString(),
        },
      };
    
    case ActionTypes.UPDATE_CARD_PROGRESS:
      const { cardId, quality } = action.payload;
      const card = state.flashcards.find(c => c.id === cardId);
      if (!card) return state;
      
      // Initialize card with spaced repetition if it doesn't exist
      const cardWithSR = card.spacedRepetition 
        ? card 
        : spacedRepetitionSystem.initializeCard(card);
      
      // Only advance if quality is 3 (perfect), otherwise keep same stage
      let updatedCard;
      if (quality === 3) {
        // Perfect score: use spaced repetition system to advance
        updatedCard = spacedRepetitionSystem.reviewCard(cardWithSR, 100);
      } else {
        // Not perfect: keep same stage, just update last reviewed
        updatedCard = {
          ...cardWithSR,
          spacedRepetition: {
            ...cardWithSR.spacedRepetition,
            lastReviewed: spacedRepetitionSystem.getTodayString(),
            // Keep same nextReviewDate and currentStage
          }
        };
      }
      
      return {
        ...state,
        flashcards: state.flashcards.map(c => 
          c.id === cardId ? updatedCard : c
        ),
      };
    
    case ActionTypes.UPDATE_STREAK:
      return {
        ...state,
        user: { ...state.user, streak: action.payload },
      };
    
    case ActionTypes.UPDATE_STUDY_TIME:
      return {
        ...state,
        user: { ...state.user, totalStudyTime: action.payload },
      };
    
    case ActionTypes.INCREMENT_LIFELONG_CARDS:
      return {
        ...state,
        user: { ...state.user, lifelongCardsCreated: (state.user.lifelongCardsCreated || 0) + 1 },
      };
    
    case ActionTypes.INCREMENT_LIFELONG_SETS:
      return {
        ...state,
        user: { ...state.user, lifelongSetsCreated: (state.user.lifelongSetsCreated || 0) + 1 },
      };
    
    case ActionTypes.INCREMENT_LIFELONG_CLASSES:
      return {
        ...state,
        user: { ...state.user, lifelongClassesCreated: (state.user.lifelongClassesCreated || 0) + 1 },
      };
    
    case ActionTypes.MOVE_SET_TO_STUDIED:
      const { setId: studiedSetId, nextReviewDate, studyCycle } = action.payload;
      const setToMove = state.flashcardSets.find(set => set.id === studiedSetId);
      if (!setToMove) return state;
      
      const remainingActiveSets = state.flashcardSets.filter(set => set.id !== studiedSetId);
      const updatedSetToMove = {
        ...setToMove,
        nextReviewDate,
        studyCycle: studyCycle !== undefined ? studyCycle : (setToMove.studyCycle || 0),
        // Keep existing lastStudied, don't update it here - it should be updated when the set is actually studied
      };
      const updatedStudiedSets = [...(state.studiedSets || []), updatedSetToMove];
      
      // Update class statistics after moving set
      const updatedClassesAfterMoveToStudied = state.classes.map(cls => {
        const allClassSets = [
          ...remainingActiveSets.filter(set => set.classId === cls.id),
          ...updatedStudiedSets.filter(set => set.classId === cls.id)
        ];
        const totalCards = allClassSets.reduce((sum, set) => sum + (set.totalCards || 0), 0);
        const totalSets = allClassSets.length;
        const lastStudied = allClassSets
          .filter(set => set.lastStudied)
          .sort((a, b) => new Date(b.lastStudied) - new Date(a.lastStudied))[0]?.lastStudied || null;
        
        return {
          ...cls,
          totalCards,
          totalSets,
          lastStudied,
        };
      });
      
      return {
        ...state,
        flashcardSets: remainingActiveSets,
        studiedSets: updatedStudiedSets,
        classes: updatedClassesAfterMoveToStudied,
      };
    
    case ActionTypes.MOVE_SET_TO_ACTIVE: {
      const { setId: activeSetId } = action.payload;
      const studiedSetToMove = (state.studiedSets || []).find(set => set.id === activeSetId);
      if (!studiedSetToMove) return state;
      
      const { nextReviewDate: _, studyCycle: currentCycle, ...setWithoutReviewDate } = studiedSetToMove;
      // Increment study cycle when moving back to active (for next time it gets 100%)
      const updatedSet = {
        ...setWithoutReviewDate,
        studyCycle: (currentCycle || 0) + 1,
      };
      const remainingStudiedSets = (state.studiedSets || []).filter(set => set.id !== activeSetId);
      const updatedActiveSets = [...state.flashcardSets, updatedSet];
      
      // Update class statistics after moving set
      const updatedClassesAfterMoveToActive = state.classes.map(cls => {
        const allClassSets = [
          ...updatedActiveSets.filter(set => set.classId === cls.id),
          ...remainingStudiedSets.filter(set => set.classId === cls.id)
        ];
        const totalCards = allClassSets.reduce((sum, set) => sum + (set.totalCards || 0), 0);
        const totalSets = allClassSets.length;
        const lastStudied = allClassSets
          .filter(set => set.lastStudied)
          .sort((a, b) => new Date(b.lastStudied) - new Date(a.lastStudied))[0]?.lastStudied || null;
        
        return {
          ...cls,
          totalCards,
          totalSets,
          lastStudied,
        };
      });
      
      return {
        ...state,
        studiedSets: remainingStudiedSets,
        flashcardSets: updatedActiveSets,
        classes: updatedClassesAfterMoveToActive,
      };
    }
    
    case ActionTypes.UPDATE_SET_STATS:
      const { setId, stats } = action.payload;
      
      
      // Update sets in flashcardSets
      const updatedSetsWithStats = state.flashcardSets.map(set => 
        set.id === setId 
          ? { 
              ...set, 
              timesPlayed: (set.timesPlayed || 0) + (stats.timesPlayed || 0),
              successRate: stats.successRate !== undefined ? stats.successRate : set.successRate,
              lastStudied: stats.lastStudied !== undefined ? stats.lastStudied : set.lastStudied,
              timeSpent: (set.timeSpent || 0) + (stats.timeSpent || 0), // Add time spent
              totalCards: stats.totalCards !== undefined ? stats.totalCards : set.totalCards, // Update total cards
            }
          : set
      );
      
      // Update sets in studiedSets as well
      const updatedStudiedSetsWithStats = (state.studiedSets || []).map(set => 
        set.id === setId 
          ? { 
              ...set, 
              timesPlayed: (set.timesPlayed || 0) + (stats.timesPlayed || 0),
              successRate: stats.successRate !== undefined ? stats.successRate : set.successRate,
              lastStudied: stats.lastStudied !== undefined ? stats.lastStudied : set.lastStudied,
              timeSpent: (set.timeSpent || 0) + (stats.timeSpent || 0), // Add time spent
              totalCards: stats.totalCards !== undefined ? stats.totalCards : set.totalCards, // Update total cards
            }
          : set
      );
      
      // Update class statistics - count sets from both active and studied
      const updatedClassesWithStats = state.classes.map(cls => {
        const classActiveSets = updatedSetsWithStats.filter(set => set.classId === cls.id);
        const classStudiedSets = updatedStudiedSetsWithStats.filter(set => set.classId === cls.id);
        const allClassSets = [...classActiveSets, ...classStudiedSets];
        const totalCards = allClassSets.reduce((sum, set) => sum + (set.totalCards || 0), 0);
        const totalSets = allClassSets.length; // Count ALL sets (active + studied)
        const lastStudied = allClassSets
          .filter(set => set.lastStudied)
          .sort((a, b) => new Date(b.lastStudied) - new Date(a.lastStudied))[0]?.lastStudied || null;
        
        return {
          ...cls,
          totalCards,
          totalSets,
          lastStudied,
        };
      });
      
      return {
        ...state,
        flashcardSets: updatedSetsWithStats,
        studiedSets: updatedStudiedSetsWithStats,
        classes: updatedClassesWithStats,
      };
    
    case ActionTypes.MARK_CLASS_FINISHED:
      const { classId, finished } = action.payload;
      const updatedClassesFinished = state.classes.map(cls => 
        cls.id === classId 
          ? { ...cls, finished: finished }
          : cls
      );
      
      return {
        ...state,
        classes: updatedClassesFinished,
      };
    
    case ActionTypes.LOAD_DATA:
      const { classes: loadedClasses, flashcardSets: loadedSets, flashcards: loadedCards, user: loadedUser, cardProgress: loadedProgress, studiedSets: loadedStudiedSets = [] } = action.payload;
      
      // Recalculate class statistics based on loaded data
      const classesWithRecalculatedStats = (loadedClasses || []).map(cls => {
        const classActiveSets = (loadedSets || []).filter(set => set.classId === cls.id);
        const classStudiedSets = (loadedStudiedSets || []).filter(set => set.classId === cls.id);
        const allClassSets = [...classActiveSets, ...classStudiedSets];
        
        // Calculate total cards for each set based on actual flashcards
        const setsWithCardCounts = allClassSets.map(set => {
          const setCards = (loadedCards || []).filter(card => card.setId === set.id);
          return {
            ...set,
            totalCards: setCards.length,
          };
        });
        
        const totalCards = setsWithCardCounts.reduce((sum, set) => sum + (set.totalCards || 0), 0);
        const totalSets = setsWithCardCounts.length;
        const lastStudied = setsWithCardCounts
          .filter(set => set.lastStudied)
          .sort((a, b) => new Date(b.lastStudied) - new Date(a.lastStudied))[0]?.lastStudied || null;
        
        return {
          ...cls,
          totalCards,
          totalSets,
          lastStudied,
        };
      });
      
      // Update sets with correct card counts
      const setsWithCorrectCounts = (loadedSets || []).map(set => {
        const setCards = (loadedCards || []).filter(card => card.setId === set.id);
        return {
          ...set,
          totalCards: setCards.length,
        };
      });
      
      const studiedSetsWithCorrectCounts = (loadedStudiedSets || []).map(set => {
        const setCards = (loadedCards || []).filter(card => card.setId === set.id);
        return {
          ...set,
          totalCards: setCards.length,
        };
      });
      
      return {
        ...state,
        classes: classesWithRecalculatedStats,
        flashcardSets: setsWithCorrectCounts,
        studiedSets: studiedSetsWithCorrectCounts,
        flashcards: loadedCards || [],
        user: loadedUser || state.user,
        cardProgress: loadedProgress || {},
      };
    
    case ActionTypes.USE_AI_FLASHCARDS:
      const numCards = action.payload;
      const currentUsed = state.user.ai_flashcards_used || 0;
      const newUsed = currentUsed + numCards;
      
      return {
        ...state,
        user: {
          ...state.user,
          ai_flashcards_used: newUsed,
        },
      };
    
    case ActionTypes.UPDATE_AI_FLASHCARD_COUNTS:
      const { ai_flashcards_earned, ai_flashcards_used } = action.payload;
      return {
        ...state,
        user: {
          ...state.user,
          ai_flashcards_earned: ai_flashcards_earned !== undefined ? ai_flashcards_earned : state.user.ai_flashcards_earned,
          ai_flashcards_used: ai_flashcards_used !== undefined ? ai_flashcards_used : state.user.ai_flashcards_used,
        },
      };
    
    case ActionTypes.CLEAR_ALL_DATA:
      return initialState;
    
    case ActionTypes.UPDATE_SUBSCRIPTION_STATUS:
      return {
        ...state,
        user: {
          ...state.user,
          isPro: action.payload.isPro,
          subscriptionType: action.payload.subscriptionType,
          subscriptionPlatform: action.payload.subscriptionPlatform,
          subscriptionExpirationDate: action.payload.subscriptionExpirationDate,
        },
      };
    
    default:
      return state;
  }
};

// SM-2 Spaced Repetition Algorithm
const calculateNextReview = (card, quality) => {
  let { ease, interval, repetitions } = card;
  
  if (quality < 3) {
    // Failed - reset repetitions but keep ease
    repetitions = 0;
    interval = 1;
  } else {
    // Passed
    repetitions += 1;
    
    if (repetitions === 1) {
      interval = 1;
    } else if (repetitions === 2) {
      interval = 6;
    } else {
      interval = Math.round(interval * ease);
    }
    
    // Update ease factor
    ease = ease + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
    ease = Math.max(1.3, ease);
  }
  
  const nextReview = new Date();
  nextReview.setDate(nextReview.getDate() + interval);
  
  return {
    ...card,
    ease,
    interval,
    repetitions,
    nextReview: nextReview.toISOString(),
    totalReviews: card.totalReviews + 1,
    correctReviews: quality >= 3 ? card.correctReviews + 1 : card.correctReviews,
  };
};

// Get cards that need review (neuroscience-based)
const getCardsForReview = (flashcards, cardProgress) => {
  const now = new Date();
  return flashcards.filter(card => {
    const progress = cardProgress[card.id];
    if (!progress) return true; // New cards
    
    const nextReview = new Date(progress.nextReview);
    return nextReview <= now;
  });
};

// Context
const FlashcardContext = createContext();

// Provider Component
export const FlashcardProvider = ({ children }) => {
  const [state, dispatch] = useReducer(flashcardReducer, initialState);

  // Load data from AsyncStorage on app start
  useEffect(() => {
    loadData();
  }, []);

  // Check and move sets to active when their nextReviewDate arrives
  useEffect(() => {
    const checkSets = () => {
      // Get today's date as YYYY-MM-DD string using device's local date
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayString = `${year}-${month}-${day}`;
      
      const setsToMove = (state.studiedSets || []).filter(set => 
        set.nextReviewDate && set.nextReviewDate <= todayString
      );
      
      setsToMove.forEach(set => {
        dispatch({ type: ActionTypes.MOVE_SET_TO_ACTIVE, payload: { setId: set.id } });
      });
    };
    
    // Check immediately
    checkSets();
    
    // Check every hour
    const interval = setInterval(checkSets, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [state.studiedSets]);

  // Sync Firestore data when user logs in (after AsyncStorage load completes)
  // Also clear data when user logs out
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User logged in - load their specific data then sync from Firestore
        console.log('👤 User logged in:', user.uid);
        
        // Load user-specific data from AsyncStorage
        loadData();
        
        // Wait a bit for loadData to complete, then sync from Firestore
        // This ensures Firestore data (source of truth) overwrites any stale AsyncStorage data
        setTimeout(async () => {
          try {
            console.log('🔄 Syncing user data from Firestore for user:', user.uid);
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const earned = userData.ai_flashcards_earned !== undefined ? userData.ai_flashcards_earned : 0;
              const used = userData.ai_flashcards_used || 0;
              const name = userData.name || user.displayName || '';
              const email = userData.email || user.email || '';
              // Ensure totalStudyTime is in minutes (not milliseconds)
              // If the value is suspiciously large (> 1000000), it might be in milliseconds
              let totalStudyTime = userData.totalStudyTime || 0;
              if (totalStudyTime > 1000000) {
                // Likely stored in milliseconds, convert to minutes
                totalStudyTime = Math.round(totalStudyTime / 60000);
              }
              
              // Sync subscription status
              const isPro = userData.isPro || false;
              const subscriptionType = userData.subscriptionType || null;
              const subscriptionPlatform = userData.subscriptionPlatform || null;
              const subscriptionExpirationDate = userData.subscriptionExpirationDate?.toDate() || null;

              console.log('📊 Firestore user data:', { name, email, earned, used, totalStudyTime, isPro });
              
              // Update user info and AI flashcard counts
              dispatch({
                type: ActionTypes.SET_USER,
                payload: {
                  name: name,
                  email: email,
                  ai_flashcards_earned: earned,
                  ai_flashcards_used: used,
                  totalStudyTime: totalStudyTime,
                },
              });

              // Update subscription status
              dispatch({
                type: ActionTypes.UPDATE_SUBSCRIPTION_STATUS,
                payload: {
                  isPro,
                  subscriptionType,
                  subscriptionPlatform,
                  subscriptionExpirationDate,
                },
              });
              
              console.log('✅ User data and subscription status synced from Firestore');
            } else {
              // User document doesn't exist - create it with default values
              // This handles existing users who signed up before document creation was added
              console.log('⚠️ User document does not exist. Creating with default values...');
              
              try {
                await setDoc(userDocRef, {
                  uid: user.uid,
                  email: user.email || null,
                  name: user.displayName || '',
                  createdAt: new Date(),
                  totalStudyTime: 0,
                  streak: 0,
                  lifelongCardsCreated: 0,
                  lifelongSetsCreated: 0,
                  lifelongClassesCreated: 0,
                  referrals_count: 0,
                  referred_by: null,
                  referral_bonus_claimed: false,
                  ai_flashcards_earned: 150, // Give existing users 150 flashcards
                  ai_flashcards_used: 0,
                  isOnboarded: false,
                });
                
                console.log('✅ User document created with 150 AI flashcards');
                
                // Update state with the new values
                dispatch({
                  type: ActionTypes.SET_USER,
                  payload: {
                    name: user.displayName || '',
                    email: user.email || '',
                    ai_flashcards_earned: 150,
                    ai_flashcards_used: 0,
                  },
                });
              } catch (createError) {
                console.error('❌ Failed to create user document:', createError.message);
              }
            }
          } catch (error) {
            if (error.code !== 'permission-denied' && !error.message?.includes('permission')) {
              console.error('❌ Could not sync user data from Firestore:', error.message, error);
            } else {
              console.warn('⚠️ Permission denied reading Firestore user data');
            }
          }
        }, 1000); // Increased delay to ensure loadData completes
      } else {
        // User logged out - reset state (but keep user's data in AsyncStorage with their user ID)
        console.log('👋 User logged out, resetting state...');
        
        // Just reset state to initial - data is saved with user ID prefix and will be restored on next login
        dispatch({ type: ActionTypes.CLEAR_ALL_DATA });
        console.log('✅ State reset to initial');
      }
    });
    
    return () => unsubscribe();
  }, []); // Run once on mount

  // Save data to AsyncStorage whenever state changes
  useEffect(() => {
    saveData();
  }, [state]);

  const loadData = async () => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      
      // Get user-specific keys if user is logged in
      const currentUser = auth.currentUser;
      const userPrefix = currentUser ? `user_${currentUser.uid}_` : '';
      
      let loadedData = null;
      
      // If user is logged in, try to load from Firestore first
      if (currentUser) {
        console.log('🔄 Loading data from Firestore for user:', currentUser.uid);
        const firestoreData = await firestoreSyncService.loadAllData(currentUser.uid);
        
        if (firestoreData) {
          console.log('✅ Loaded data from Firestore');
          loadedData = firestoreData;
        } else {
          console.log('ℹ️ No Firestore data, checking localStorage...');
          
          // Load from localStorage for potential migration
          const [classesData, setsData, cardsData, progressData, studiedSetsData] = await Promise.all([
            storage.getItem(`${userPrefix}classes`),
            storage.getItem(`${userPrefix}flashcardSets`),
            storage.getItem(`${userPrefix}flashcards`),
            storage.getItem(`${userPrefix}cardProgress`),
            storage.getItem(`${userPrefix}studiedSets`),
          ]);
          
          const localData = {
            classes: classesData ? JSON.parse(classesData) : [],
            flashcardSets: setsData ? JSON.parse(setsData) : [],
            flashcards: cardsData ? JSON.parse(cardsData) : [],
            cardProgress: progressData ? JSON.parse(progressData) : {},
            studiedSets: studiedSetsData ? JSON.parse(studiedSetsData) : [],
          };
          
          // Check if localStorage has data to migrate
          const hasLocalData = localData.classes.length > 0 || 
                               localData.flashcardSets.length > 0 || 
                               localData.flashcards.length > 0;
          
          if (hasLocalData) {
            console.log('📦 Found localStorage data, migrating to Firestore...');
            await firestoreSyncService.migrateLocalStorageToFirestore(currentUser.uid, localData);
            loadedData = localData;
          } else {
            console.log('ℹ️ No data found in localStorage or Firestore');
            loadedData = {
              classes: [],
              flashcardSets: [],
              flashcards: [],
              cardProgress: {},
              studiedSets: [],
            };
          }
        }
      } else {
        // Not logged in - load from localStorage
        console.log('📱 Loading data from localStorage (not logged in)');
        const [classesData, setsData, cardsData, progressData, studiedSetsData] = await Promise.all([
          storage.getItem(`${userPrefix}classes`),
          storage.getItem(`${userPrefix}flashcardSets`),
          storage.getItem(`${userPrefix}flashcards`),
          storage.getItem(`${userPrefix}cardProgress`),
          storage.getItem(`${userPrefix}studiedSets`),
        ]);
        
        loadedData = {
          classes: classesData ? JSON.parse(classesData) : [],
          flashcardSets: setsData ? JSON.parse(setsData) : [],
          flashcards: cardsData ? JSON.parse(cardsData) : [],
          cardProgress: progressData ? JSON.parse(progressData) : {},
          studiedSets: studiedSetsData ? JSON.parse(studiedSetsData) : [],
        };
        
        // FOR DEMO: Initialize demo data if in demo mode and no data exists
        const isDemoMode = typeof window !== 'undefined' && localStorage.getItem('isDemoMode') === 'true';
        if (isDemoMode && loadedData.classes.length === 0) {
          console.log('🎮 Initializing demo data...');
          const demoClassId = `demo-class-${Date.now()}`;
          const demoSetId = `demo-set-${Date.now()}`;
          
          // Create "Get Started" class
          const demoClass = {
            id: demoClassId,
            name: 'Get Started',
            color: '#60A5FA',
            createdAt: new Date().toISOString(),
            totalSets: 1,
            totalCards: 3,
            lastStudied: null,
          };
          
          // Create "Flashcard" set
          const demoSet = {
            id: demoSetId,
            name: 'Flashcard',
            classId: demoClassId,
            createdAt: new Date().toISOString(),
            totalCards: 3,
            timesPlayed: 0,
            lastStudied: null,
            difficulty: 'medium',
          };
          
          // Create demo flashcards that explain the app
          const demoFlashcards = [
            {
              id: `demo-card-1-${Date.now()}`,
              setId: demoSetId,
              classId: demoClassId,
              front: 'What is SpacedAI?',
              back: 'SpacedAI is a flashcard app that uses AI to generate flashcards and spaced repetition to help you learn more effectively. It combines neuroscience-based algorithms with AI-powered content creation.',
              hint: '',
              difficulty: 'easy',
              createdAt: new Date().toISOString(),
            },
            {
              id: `demo-card-2-${Date.now() + 1}`,
              setId: demoSetId,
              classId: demoClassId,
              front: 'How does spaced repetition work?',
              back: 'Spaced repetition shows you flashcards at increasing intervals. Cards you know well appear less frequently, while difficult cards appear more often. This optimizes memory retention based on the forgetting curve.',
              hint: '',
              difficulty: 'medium',
              createdAt: new Date().toISOString(),
            },
            {
              id: `demo-card-3-${Date.now() + 2}`,
              setId: demoSetId,
              classId: demoClassId,
              front: 'How do I create flashcards?',
              back: 'You can create flashcards manually, or use AI to generate them from any topic or document. Just click "AI Generate" to create flashcards instantly from text or topics.',
              hint: '',
              difficulty: 'easy',
              createdAt: new Date().toISOString(),
            },
          ];
          
          loadedData = {
            classes: [demoClass],
            flashcardSets: [demoSet],
            flashcards: demoFlashcards,
            cardProgress: {},
            studiedSets: [],
          };
          
          // Save demo data to localStorage
          await Promise.all([
            storage.setItem(`${userPrefix}classes`, JSON.stringify(loadedData.classes)),
            storage.setItem(`${userPrefix}flashcardSets`, JSON.stringify(loadedData.flashcardSets)),
            storage.setItem(`${userPrefix}flashcards`, JSON.stringify(loadedData.flashcards)),
          ]);
          
          console.log('✅ Demo data initialized');
        }
      }
      
      // Load user data from localStorage (or use default)
      const userData = await storage.getItem(`${userPrefix}user`);
      const parsedUserData = userData ? JSON.parse(userData) : null;
      
      // If user is logged in, don't load AI flashcard counts from localStorage
      // They will be synced from Firestore (source of truth)
      const isLoggedIn = auth.currentUser !== null;
      const isDemoMode = !isLoggedIn && typeof window !== 'undefined' && localStorage.getItem('isDemoMode') === 'true';
      let userDataToLoad = parsedUserData || initialState.user;
      
      // Demo mode users get 20 AI flashcards
      if (isDemoMode && !parsedUserData) {
        userDataToLoad = {
          ...initialState.user,
          name: 'Demo User',
          ai_flashcards_earned: 20,
          ai_flashcards_used: 0,
          isOnboarded: true,
        };
      }
      
      if (isLoggedIn && parsedUserData) {
        // Remove AI flashcard counts from localStorage data - will come from Firestore
        const { ai_flashcards_earned, ai_flashcards_used, ...userWithoutAI } = parsedUserData;
        userDataToLoad = {
          ...userWithoutAI,
          isOnboarded: Boolean(parsedUserData.isOnboarded),
          // Keep initial state values, Firestore sync will update them
          ai_flashcards_earned: initialState.user.ai_flashcards_earned,
          ai_flashcards_used: initialState.user.ai_flashcards_used,
        };
      } else if (parsedUserData) {
        // For logged out users, use localStorage values
        userDataToLoad = {
          ...parsedUserData,
          isOnboarded: Boolean(parsedUserData.isOnboarded),
        };
      }
      
      // Add user data to loaded data
      loadedData.user = userDataToLoad;

      // Dispatch LOAD_DATA action which will recalculate class statistics
      dispatch({ type: ActionTypes.LOAD_DATA, payload: loadedData });

      // After loading, check if any studied sets should be moved back to active
      setTimeout(() => {
        // Get today's date as YYYY-MM-DD string using device's local date
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const todayString = `${year}-${month}-${day}`;
        
        const setsToMove = (loadedData.studiedSets || []).filter(set => 
          set.nextReviewDate && set.nextReviewDate <= todayString
        );
        
        setsToMove.forEach(set => {
          dispatch({ type: ActionTypes.MOVE_SET_TO_ACTIVE, payload: { setId: set.id } });
        });
      }, 100);

    } catch (error) {
      console.error('❌ Failed to load data:', error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: 'Failed to load data' });
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  };

  const saveData = async () => {
    try {
      // Get user-specific keys if user is logged in
      const currentUser = auth.currentUser;
      const userPrefix = currentUser ? `user_${currentUser.uid}_` : '';
      
      // Save to localStorage immediately (fast, synchronous-like)
      await Promise.all([
        storage.setItem(`${userPrefix}classes`, JSON.stringify(state.classes)),
        storage.setItem(`${userPrefix}flashcardSets`, JSON.stringify(state.flashcardSets)),
        storage.setItem(`${userPrefix}flashcards`, JSON.stringify(state.flashcards)),
        storage.setItem(`${userPrefix}user`, JSON.stringify({
          ...state.user,
          isOnboarded: Boolean(state.user.isOnboarded),
        })),
        storage.setItem(`${userPrefix}cardProgress`, JSON.stringify(state.cardProgress)),
        storage.setItem(`${userPrefix}studiedSets`, JSON.stringify(state.studiedSets || [])),
      ]);
      
      // If user is logged in, also sync to Firestore (debounced to reduce writes)
      if (currentUser) {
        firestoreSyncService.debouncedSave(currentUser.uid, 'all', {
          classes: state.classes,
          flashcardSets: state.flashcardSets,
          flashcards: state.flashcards,
          cardProgress: state.cardProgress,
          studiedSets: state.studiedSets || [],
        }, 3000); // Wait 3 seconds before saving to Firestore (batch multiple changes)
      }
    } catch (error) {
      console.warn('⚠️ Error saving data:', error);
      // Silently handle error - app continues to work
    }
  };

  // Action Creators
  const actions = {
    setUser: (user) => {
      const userData = {
        ...user,
        isOnboarded: Boolean(user.isOnboarded),
      };
      dispatch({ type: ActionTypes.SET_USER, payload: userData });
    },
    
    setOnboarded: (isOnboarded) => {
      dispatch({ type: ActionTypes.SET_ONBOARDED, payload: Boolean(isOnboarded) });
    },
    
    addClass: (classData) => {
      dispatch({ type: ActionTypes.ADD_CLASS, payload: classData });
    },
    
    updateClass: (classData) => {
      dispatch({ type: ActionTypes.UPDATE_CLASS, payload: classData });
    },
    
    deleteClass: (classId) => {
      dispatch({ type: ActionTypes.DELETE_CLASS, payload: classId });
    },
    
    setCurrentClass: (classData) => {
      dispatch({ type: ActionTypes.SET_CURRENT_CLASS, payload: classData });
    },
    
    setCurrentSet: (setData) => {
      dispatch({ type: ActionTypes.SET_CURRENT_SET, payload: setData });
    },
    
    addFlashcardSet: (setData) => {
      dispatch({ type: ActionTypes.ADD_FLASHCARD_SET, payload: setData });
    },
    
    updateFlashcardSet: (setData) => {
      dispatch({ type: ActionTypes.UPDATE_FLASHCARD_SET, payload: setData });
    },
    
    deleteFlashcardSet: (setId) => {
      dispatch({ type: ActionTypes.DELETE_FLASHCARD_SET, payload: setId });
    },
    
    addFlashcard: (cardData) => {
      dispatch({ type: ActionTypes.ADD_FLASHCARD, payload: cardData });
    },
    
    addMultipleFlashcards: (setId, flashcards) => {
      dispatch({ type: ActionTypes.ADD_MULTIPLE_FLASHCARDS, payload: { setId, flashcards } });
    },
    
    updateFlashcard: (cardData) => {
      dispatch({ type: ActionTypes.UPDATE_FLASHCARD, payload: cardData });
    },
    
    deleteFlashcard: (cardId) => {
      dispatch({ type: ActionTypes.DELETE_FLASHCARD, payload: cardId });
    },
    
    startStudySession: () => {
      dispatch({ type: ActionTypes.START_STUDY_SESSION });
    },
    
    endStudySession: async () => {
      const currentUser = auth.currentUser;
      const sessionStartTime = state.studySession.sessionStartTime;
      
      // Calculate session duration (same logic as reducer)
      let sessionDurationMinutes = 0;
      if (sessionStartTime) {
        try {
          const sessionStart = new Date(sessionStartTime);
          const sessionEnd = new Date();
          const sessionDuration = sessionEnd - sessionStart;
          
          if (sessionDuration > 0 && sessionDuration < 86400000) {
            sessionDurationMinutes = Math.round(sessionDuration / 60000);
          }
        } catch (error) {
          console.error('Error calculating session duration:', error);
        }
      }
      
      // Calculate new total study time (same logic as reducer)
      const currentTotalTime = Number(state.user.totalStudyTime) || 0;
      const normalizedTotalTime = currentTotalTime > 1000000 
        ? Math.round(currentTotalTime / 60000) 
        : currentTotalTime;
      const newTotalTime = normalizedTotalTime + sessionDurationMinutes;
      
      // Update local state
      dispatch({ type: ActionTypes.END_STUDY_SESSION });
      
      // Save to Firestore asynchronously (don't wait for it)
      if (currentUser && sessionDurationMinutes > 0) {
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          await updateDoc(userDocRef, {
            totalStudyTime: newTotalTime,
            lastStudyDate: new Date().toISOString(),
          });
          console.log('✅ Study time saved to Firestore:', newTotalTime, 'minutes');
        } catch (error) {
          console.warn('Could not update study time in Firestore:', error.message);
          // Don't fail if Firestore update fails - local state is already updated
        }
      }
    },
    
    updateCardProgress: (cardId, quality) => {
      dispatch({ type: ActionTypes.UPDATE_CARD_PROGRESS, payload: { cardId, quality } });
    },
    
    updateStreak: (streak) => {
      dispatch({ type: ActionTypes.UPDATE_STREAK, payload: streak });
    },
    
    // Calculate and update streak based on study activity
    updateStreakFromStudy: () => {
      const today = new Date().toDateString();
      const lastStudyDate = state.user.lastStudyDate;
      
      if (lastStudyDate === today) {
        // Already studied today, no streak update needed
        return;
      }
      
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayString = yesterday.toDateString();
      
      let newStreak = 0;
      
      if (!lastStudyDate) {
        // First time studying, start streak at 1
        newStreak = 1;
      } else if (lastStudyDate === yesterdayString) {
        // Studied yesterday, increment streak
        newStreak = state.user.streak + 1;
      } else {
        // Missed a day or more, reset streak to 1
        newStreak = 1;
      }
      
      dispatch({ 
        type: ActionTypes.UPDATE_STREAK, 
        payload: newStreak 
      });
      
      dispatch({ 
        type: ActionTypes.UPDATE_USER, 
        payload: { lastStudyDate: today } 
      });
    },
    
    updateStudyTime: (time) => {
      dispatch({ type: ActionTypes.UPDATE_STUDY_TIME, payload: time });
    },
    
    updateSetStats: (setId, stats) => {
      dispatch({ type: ActionTypes.UPDATE_SET_STATS, payload: { setId, stats } });
    },
    
    markClassFinished: (classId, finished) => {
      dispatch({ type: ActionTypes.MARK_CLASS_FINISHED, payload: { classId, finished } });
    },
    
    incrementLifelongCards: () => {
      dispatch({ type: ActionTypes.INCREMENT_LIFELONG_CARDS });
    },
    
    incrementLifelongSets: () => {
      dispatch({ type: ActionTypes.INCREMENT_LIFELONG_SETS });
    },
    
    incrementLifelongClasses: () => {
      dispatch({ type: ActionTypes.INCREMENT_LIFELONG_CLASSES });
    },
    
    // AI Flashcard methods
    getRemainingAIFlashcards: () => {
      // Pro users have unlimited AI flashcards
      if (state.user.isPro) {
        return Infinity;
      }
      
      // Demo mode users get 20 AI flashcards
      const isDemoMode = typeof window !== 'undefined' && localStorage.getItem('isDemoMode') === 'true';
      if (isDemoMode && !auth.currentUser) {
        const used = state.user.ai_flashcards_used || 0;
        return Math.max(0, 20 - used);
      }
      
      // Free users have a limit
      const earned = state.user.ai_flashcards_earned || 0;
      const used = state.user.ai_flashcards_used || 0;
      return Math.max(0, earned - used);
    },
    
    canUseAIFlashcards: (numCards) => {
      const remaining = actions.getRemainingAIFlashcards();
      return remaining >= numCards;
    },

    // Check if user can create a new class
    canCreateClass: () => {
      // All users (free and pro) can create unlimited classes
      return true;
    },

    // Check if user can create a new set in a class
    canCreateSet: (classId) => {
      // Pro users have unlimited sets
      if (state.user.isPro) {
        return true;
      }
      
      // Free users limited to 5 sets per class
      const classSets = state.flashcardSets.filter(set => set.classId === classId);
      return classSets.length < 5;
    },

    // Check if user is Pro
    isPro: () => {
      return state.user.isPro || false;
    },
    
    useAIFlashcards: async (numCards) => {
      if (!actions.canUseAIFlashcards(numCards)) {
        return false;
      }
      
      // Update local state
      dispatch({ type: ActionTypes.USE_AI_FLASHCARDS, payload: numCards });
      
      // Update Firestore asynchronously (don't wait for it)
      try {
        const user = auth.currentUser;
        if (user) {
          const userDocRef = doc(db, 'users', user.uid);
          await updateDoc(userDocRef, {
            ai_flashcards_used: increment(numCards),
          });
        }
      } catch (error) {
        // Handle permission errors gracefully - app continues to work with local state
        if (error.code !== 'permission-denied' && !error.message?.includes('permission')) {
          // Only log non-permission errors
          console.warn('Could not update AI flashcard usage in Firestore:', error.message);
        }
        // Don't fail if Firestore update fails - local state is already updated
      }
      
      return true;
    },
    
    updateAIFlashcardCounts: (counts) => {
      dispatch({ type: ActionTypes.UPDATE_AI_FLASHCARD_COUNTS, payload: counts });
    },
    
    // Sync user data from Firestore (especially AI flashcard counts)
    // IMPORTANT: This only reads from Firestore - does NOT modify or set default values
    // The 150 AI flashcards are only granted ONCE during signup in AuthContext
    syncUserDataFromFirestore: async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Read whatever is in Firestore - don't modify or set defaults
          // If the value is missing, it means the user signed up before this feature was added
          // They will have 0 and can earn more through referrals or upgrade to Pro
          const earned = userData.ai_flashcards_earned !== undefined ? userData.ai_flashcards_earned : 0;
          const used = userData.ai_flashcards_used || 0;
          
          dispatch({
            type: ActionTypes.UPDATE_AI_FLASHCARD_COUNTS,
            payload: {
              ai_flashcards_earned: earned,
              ai_flashcards_used: used,
            },
          });

          // Sync subscription status
          const isPro = userData.isPro || false;
          const subscriptionType = userData.subscriptionType || null;
          const subscriptionPlatform = userData.subscriptionPlatform || null;
          const subscriptionExpirationDate = userData.subscriptionExpirationDate?.toDate() || null;

          dispatch({
            type: ActionTypes.UPDATE_SUBSCRIPTION_STATUS,
            payload: {
              isPro,
              subscriptionType,
              subscriptionPlatform,
              subscriptionExpirationDate,
            },
          });
        }
        // If user document doesn't exist, don't set defaults here
        // It will be created during registration with 150 flashcards
      } catch (error) {
        // Handle permission errors gracefully - this happens if Firestore rules don't allow user read access
        if (error.code === 'permission-denied' || error.message?.includes('permission')) {
          // Permission denied - don't update state, let it use whatever is in local storage
          return;
        }
        // For other errors, log but don't break the app or change state
        console.warn('Could not sync user data from Firestore:', error.message);
      }
    },
    
    moveSetToStudied: (setId, nextReviewDate, studyCycle) => {
      dispatch({ type: ActionTypes.MOVE_SET_TO_STUDIED, payload: { setId, nextReviewDate, studyCycle } });
    },
    
    moveSetToActive: (setId) => {
      dispatch({ type: ActionTypes.MOVE_SET_TO_ACTIVE, payload: { setId } });
    },
    
    checkAndMoveSetsToActive: () => {
      // Get today's date as YYYY-MM-DD string using device's local date
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayString = `${year}-${month}-${day}`;
      
      const setsToMove = (state.studiedSets || []).filter(set => 
        set.nextReviewDate && set.nextReviewDate <= todayString
      );
      
      setsToMove.forEach(set => {
        dispatch({ type: ActionTypes.MOVE_SET_TO_ACTIVE, payload: { setId: set.id } });
      });
    },
    
    // Force immediate sync to Firestore (useful for important actions)
    forceSyncToFirestore: async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.log('ℹ️ Not logged in, skipping Firestore sync');
        return;
      }
      
      try {
        console.log('🔄 Force syncing to Firestore...');
        await firestoreSyncService.saveAllData(currentUser.uid, {
          classes: state.classes,
          flashcardSets: state.flashcardSets,
          flashcards: state.flashcards,
          cardProgress: state.cardProgress,
          studiedSets: state.studiedSets || [],
        });
        console.log('✅ Force sync completed');
      } catch (error) {
        console.error('❌ Force sync failed:', error);
      }
    },
    
    // Load data from Firestore (refresh from cloud)
    loadFromFirestore: async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.log('ℹ️ Not logged in, skipping Firestore load');
        return;
      }
      
      try {
        console.log('🔄 Loading data from Firestore...');
        const firestoreData = await firestoreSyncService.loadAllData(currentUser.uid);
        
        if (firestoreData) {
          // Load user data separately
          const userData = await storage.getItem(`user_${currentUser.uid}_user`);
          const parsedUserData = userData ? JSON.parse(userData) : null;
          
          dispatch({ 
            type: ActionTypes.LOAD_DATA, 
            payload: {
              ...firestoreData,
              user: parsedUserData || state.user,
            }
          });
          console.log('✅ Data loaded from Firestore');
        } else {
          console.log('ℹ️ No Firestore data to load');
        }
      } catch (error) {
        console.error('❌ Failed to load from Firestore:', error);
      }
    },
    
    // Clear all data (admin function - not used on logout anymore)
    clearAllData: async () => {
      try {
        console.log('🗑️ Clearing all stored data...');
        
        // Get all keys and clear everything
        const keys = await storage.getAllKeys();
        await storage.multiRemove(keys);
        
        // Reset state to initial
        dispatch({ type: ActionTypes.CLEAR_ALL_DATA });
        
        console.log('✅ All stored data cleared');
      } catch (error) {
        console.error('❌ Error clearing data:', error);
      }
    },
  };

  return (
    <FlashcardContext.Provider value={{ ...state, ...actions }}>
      {children}
    </FlashcardContext.Provider>
  );
};

// Custom Hook
export const useFlashcards = () => {
  const context = useContext(FlashcardContext);
  if (!context) {
    throw new Error('useFlashcards must be used within a FlashcardProvider');
  }
  return context;
};